'use client';
import { Fragment, useLayoutEffect, useRef } from 'react';
import { useDefaultLayout, useGroupRef, type Layout } from 'react-resizable-panels';
import type { ResizablePanelsProps } from 'elowen-plugin-ui-kit';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from './shadcn/resizable';

/** Whether a committed layout's shares fill the group, within the 0.1 the library itself allows a whole
 *  layout (it drops a resize step that would leave one outside it). */
function fillsGroup(layout: Layout): boolean {
  return Math.abs(Object.values(layout).reduce((sum, share) => sum + share, 0) - 100) <= 0.1;
}

/** The width the library shares out among the panes: the group's panels, without its separators. */
function panesWidth(group: HTMLElement): number {
  let width = 0;
  for (const child of group.children) if (child.hasAttribute('data-panel')) width += (child as HTMLElement).offsetWidth;
  return width;
}

/**
 * A horizontal split of two or more panes, every divider draggable: the plugin-facing composition of the
 * `react-resizable-panels` wrapper in `shadcn/resizable.tsx`, the same primitive the chat telemetry rail
 * sits on.
 *
 * ONE group, its panes as direct children and a shared separator between each pair; never a group nested
 * in a pane, because the library sizes and hit-tests each group on its own and a nested split would fight
 * the outer one for the same pointer. Sizes reach the library unconverted (a number is pixels, a string
 * without a unit a percentage of the group). The library also owns the drag, the arrow-key steps of a
 * focused separator, the double-click reset to a pane's `defaultSize` and the `role="separator"` ARIA
 * values; this component only lays the panes out and remembers the layout.
 *
 * Persistence is the library's own `useDefaultLayout` over `localStorage`, under the caller's
 * `storageKey`: one browser-local record per surface, written after every layout commit (a drag on
 * release, a key, a reset) and read on mount. The record holds percentages of the group, so a layout
 * dragged on one window comes back proportionally on another, held to every pane's `minSize`. A commit
 * whose shares do not fill the group is never written: the library still commits one when the group is
 * narrower than the panes' minimums together (148% on a 1440 → 900 px jump), and nobody chose it.
 *
 * When the window resizes, every pane but the last keeps its pixel width and the last pane absorbs the
 * change: a sidebar and a list stay where the reader put them while the reading pane grows.
 *
 * `activePaneId` shows one pane at a time in the SAME tree, so nothing a pane holds (a half-written reply,
 * its attachments) is lost when a phone or a narrowed window switches modes. The library does the layout
 * from public props alone: every pane's size constraints pin the active pane to the whole group and the
 * others to zero, the group is `disabled` (no drag, key or double-click) and the separators leave. The
 * zero-width panes are `inert`, out of the tab order and the accessibility tree. Their ids change while
 * they are hidden because the library keeps one layout per set of panel ids: the one-pane arrangement is
 * then a set of its own and never replaces the side-by-side one. Nothing is written to storage meanwhile.
 * The way back restores the pixel widths of the last side-by-side layout that fit (below).
 */
export function ResizablePanels({ storageKey, panes, activePaneId }: ResizablePanelsProps) {
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({ id: storageKey, storage: localStorage });
  const group = useGroupRef();
  const element = useRef<HTMLDivElement>(null);
  // Pixel widths of the last side-by-side layout that fit. The way back from one pane needs them: the
  // group re-registers with the layout it last committed side by side, which is the one for the frame
  // already too narrow (the library commits it a frame before the caller switches), and percentages
  // would scale the panes, since the shell is still settling to its final width when the panes return.
  const fitted = useRef<Record<string, number> | null>(null);
  const onePane = activePaneId !== undefined;
  const wasOnePane = useRef(onePane);
  useLayoutEffect(() => {
    if (wasOnePane.current && !onePane && fitted.current) {
      // As on any resize, every pane but the last keeps its pixel width and the last takes the rest.
      const width = panesWidth(element.current!);
      const layout: Layout = {};
      let used = 0;
      for (const pane of panes.slice(0, -1)) {
        const share = fitted.current[pane.id]! / width * 100;
        layout[pane.id] = share;
        used += share;
      }
      layout[panes[panes.length - 1]!.id] = 100 - used;
      group.current!.setLayout(layout);
    }
    wasOnePane.current = onePane;
  }, [onePane, panes, group]);
  if (panes.length < 2) throw new Error(`ResizablePanels "${storageKey}" needs at least two panes.`);
  // A separator's accessible name is the only thing that says which pane it resizes; one without it is
  // an unnamed slider to a screen reader, so the caller's omission fails here rather than in an audit.
  const unnamed = panes.slice(0, -1).find((pane) => !pane.separatorLabel);
  if (unnamed) throw new Error(`ResizablePanels "${storageKey}": pane "${unnamed.id}" needs a separatorLabel.`);
  // An id that names no pane would pin every pane to zero and leave an empty frame.
  if (activePaneId !== undefined && !panes.some((pane) => pane.id === activePaneId)) {
    throw new Error(`ResizablePanels "${storageKey}": activePaneId "${activePaneId}" names no pane.`);
  }
  const last = panes.length - 1;

  return (
    <ResizablePanelGroup
      orientation="horizontal"
      groupRef={group}
      elementRef={element}
      disabled={onePane}
      defaultLayout={defaultLayout}
      onLayoutChanged={onePane ? undefined : (layout, meta) => {
        if (!fillsGroup(layout)) return;
        // The commit of the re-registration on the way back runs before the restore above and is not
        // the reader's layout, so it is stored (the restore overwrites it) but never kept as fitted.
        if (!wasOnePane.current) {
          const width = panesWidth(element.current!);
          fitted.current = Object.fromEntries(Object.entries(layout).map(([id, share]) => [id, share / 100 * width]));
        }
        onLayoutChanged(layout, meta);
      }}
    >
      {panes.map((pane, index) => {
        const hidden = onePane && pane.id !== activePaneId;
        return (
          <Fragment key={pane.id}>
            {index > 0 && !onePane ? <ResizableHandle aria-label={panes[index - 1]!.separatorLabel} /> : null}
            <ResizablePanel
              id={hidden ? `${pane.id}:hidden` : pane.id}
              defaultSize={pane.defaultSize}
              minSize={onePane ? (hidden ? 0 : '100') : pane.minSize}
              maxSize={hidden ? 0 : undefined}
              inert={hidden}
              groupResizeBehavior={index === last ? 'preserve-relative-size' : 'preserve-pixel-size'}
            >
              {pane.children}
            </ResizablePanel>
          </Fragment>
        );
      })}
    </ResizablePanelGroup>
  );
}
